import React, { useState, useCallback, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Map, BarChart3, Activity, AlertCircle } from 'lucide-react';

// Bundle optimized components with dynamic loading
import { TrailMapLoader } from './TrailMapLoader';
import { ElevationProfileLoader } from './ElevationProfileLoader';
const TrailStats = lazy(() => import('./TrailStats'));
import { loadGPXData, TrailData } from '@/lib/gpx-loader';
import { useQuery } from '@tanstack/react-query';

interface TrailViewerProps {
  courseId: string;
  className?: string;
  courseData?: {
    estimated_time?: number | null;
    name?: string;
    difficulty?: string | null;
  };
}

const TrailViewer: React.FC<TrailViewerProps> = ({ courseId, className = '', courseData }) => {
  const [activeTab, setActiveTab] = useState('map'); // GPX 지도를 기본 탭으로 설정
  const [hoveredPoint, setHoveredPoint] = useState<{
    lat: number;
    lon: number;
    elevation: number;
    distance: number;
  } | null>(null);

  // React Query를 사용한 GPX 데이터 로딩 (최적화된 캐싱)
  const {
    data: trailData,
    isLoading,
    error,
    isError
  } = useQuery({
    queryKey: ['trail-data', courseId, courseData],
    queryFn: () => loadGPXData(courseId, courseData),
    enabled: !!courseId,
    staleTime: Infinity, // 영구 캐시 (불변 데이터)
    gcTime: Infinity, // 메모리에서 영구 보관
    retry: 3, // 재시도 횟수 증가
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000) // 지수 백오프
  });

  // course 데이터를 우선 사용, 없으면 stats의 영문 난이도를 한글로 변환
  const getDifficultyText = useCallback(() => {
    if (courseData?.difficulty) {
      return courseData.difficulty;
    }
    
    if (!trailData) return '보통';
    
    // 영문 난이도를 한글로 변환
    switch (trailData.stats.difficulty) {
      case 'Easy': return '쉬움';
      case 'Moderate': return '보통';
      case 'Hard': return '어려움';
      default: return '보통';
    }
  }, [courseData?.difficulty, trailData]);

  const getDifficultyColor = useCallback(() => {
    const difficulty = getDifficultyText();
    switch (difficulty) {
      case '쉬움': return 'text-green-600';
      case '보통': return 'text-yellow-600';
      case '어려움': return 'text-red-600';
      default: return 'text-gray-600';
    }
  }, [getDifficultyText]);

  // 고도 프로필에서 호버 시 지도에 마커 표시
  const handleElevationHover = useCallback((point: {
    lat: number;
    lon: number;
    elevation: number;
    distance: number;
  } | null) => {
    setHoveredPoint(point);
  }, []);

  // 탭 전환 애니메이션
  const tabContentVariants = {
    hidden: { 
      opacity: 0, 
      x: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      x: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      x: -20,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    }
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* 탭 스켈레톤 */}
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 flex-1" />
              ))}
            </div>
            
            {/* 컨텐츠 스켈레톤 */}
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-64 w-full" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-20" />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // 에러 상태
  if (isError || !trailData) {
    const errorMessage = error instanceof Error ? error.message : 'GPX 파일을 로드할 수 없습니다.';
    
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {errorMessage}
              <br />
              <span className="text-sm text-gray-500 mt-1 block">
                코스 ID: {courseId}
              </span>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={className}
    >
      <Card>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* 탭 리스트 */}
            <div className="border-b bg-gray-50/50">
              <TabsList className="grid w-full grid-cols-3 bg-transparent p-1">
                <TabsTrigger 
                  value="map" 
                  className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  <Map className="w-4 h-4" />
                  <span className="hidden sm:inline">GPX 기반 지도</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="elevation" 
                  className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="hidden sm:inline">고도 프로필</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="stats" 
                  className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  <Activity className="w-4 h-4" />
                  <span className="hidden sm:inline">통계</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* 탭 컨텐츠 */}
            <div className="relative overflow-hidden">
              <AnimatePresence mode="wait">
                {activeTab === 'map' && (
                  <TabsContent value="map" className="mt-0" forceMount>
                    <motion.div
                      key="map"
                      variants={tabContentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <Suspense fallback={
                        <div className="relative h-96 w-full">
                          <Skeleton className="h-full w-full rounded-lg" />
                          <div className="absolute top-4 left-4 space-y-2">
                            <Skeleton className="h-8 w-8 rounded" />
                            <Skeleton className="h-8 w-8 rounded" />
                          </div>
                          <div className="absolute bottom-4 right-4">
                            <Skeleton className="h-6 w-20" />
                          </div>
                        </div>
                      }>
                        <TrailMapLoader 
                          courseId={courseId}
                          hoveredPoint={hoveredPoint}
                        />
                      </Suspense>
                    </motion.div>
                  </TabsContent>
                )}

                {activeTab === 'elevation' && (
                  <TabsContent value="elevation" className="mt-0" forceMount>
                    <motion.div
                      key="elevation"
                      variants={tabContentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="p-6"
                    >
                      <Suspense fallback={
                        <div className="space-y-4">
                          <Skeleton className="h-6 w-40" />
                          <Skeleton className="h-64 w-full" />
                          <div className="flex justify-between items-center">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-4 w-32" />
                          </div>
                        </div>
                      }>
                        <ElevationProfileLoader 
                          trailData={trailData}
                          onHover={handleElevationHover}
                        />
                      </Suspense>
                    </motion.div>
                  </TabsContent>
                )}

                {activeTab === 'stats' && (
                  <TabsContent value="stats" className="mt-0" forceMount>
                    <motion.div
                      key="stats"
                      variants={tabContentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="p-6"
                    >
                      <Suspense fallback={
                        <div className="space-y-4">
                          <Skeleton className="h-6 w-32" />
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[1,2,3,4].map(i => <Skeleton key={i} className="h-20" />)}
                          </div>
                          <Skeleton className="h-40" />
                        </div>
                      }>
                        <TrailStats 
                          stats={trailData.stats} 
                          courseData={courseData}
                        />
                      </Suspense>
                    </motion.div>
                  </TabsContent>
                )}
              </AnimatePresence>
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* 퀵 액세스 정보 (모든 탭에서 보임) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        className="mt-4"
      >
        <Card className="bg-gradient-to-r from-blue-50 via-green-50 to-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-sm text-gray-600 mb-1">총 거리</div>
                <div className="text-lg font-semibold text-blue-600">
                  {trailData.stats.totalDistance.toFixed(1)}km
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">고도차</div>
                <div className="text-lg font-semibold text-green-600">
                  {(trailData.stats.maxElevation - trailData.stats.minElevation).toFixed(0)}m
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">예상시간</div>
                <div className="text-lg font-semibold text-purple-600">
                  {Math.floor(trailData.stats.estimatedTime)}h {Math.round((trailData.stats.estimatedTime % 1) * 60)}m
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">난이도</div>
                <div className={`text-lg font-semibold ${getDifficultyColor()}`}>
                  {getDifficultyText()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default TrailViewer;